import {
  type LayerBarProps,
  SESSIONS_TEST_IDS,
  SESSION_LAYERS,
  SESSION_LAYER_LABEL,
} from '@/components/admin/sessions/contracts';

/**
 * The sticky bar of the six switches (§12.3).
 *
 * ## One click opens a layer on the WHOLE conversation
 *
 * « Une bascule de couche ouvre ou ferme cette couche sur toute la conversation
 * d'un clic » - which is the gesture that makes a depth adjustable rather than a
 * page pre-filled. It is also why the switches travel in the URL and the « + »
 * of a single turn does not: one is a way of reading the whole session, which
 * somebody pastes into a thread; the other is a hand opening a drawer.
 *
 * ## Six switches and one of them is not an accordion
 *
 * `etat` is the sticky column and rides in the same list all the same, the
 * reason `contracts.ts` gives: « c'est le même GESTE pour un lecteur - un
 * interrupteur par nature de détail - et le séparer aurait donné à la barre cinq
 * interrupteurs et une case perdue ».
 *
 * `aria-pressed` and not a checkbox: these are toggle buttons, the form
 * `filter-bar` already uses, and what `getByRole('button', { pressed })` reads.
 * The names come from the CATALOGUE, because those strings are selectors.
 */

export function LayerBar({ open, onToggle, onOpenAll, onCloseAll }: LayerBarProps) {
  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: a <fieldset> groups form controls under a <legend>; these are toggle buttons named by their own labels - the arrangement `segmented-control` already settled.
      role="group"
      aria-label="Couches de précision"
      className="sticky top-admin-bar z-20 flex flex-wrap items-center gap-2 border-gray-200 border-b bg-white py-2.5"
    >
      <span className="mr-1 font-semibold text-gray-600 text-caption uppercase tracking-wide">
        Couches
      </span>
      {SESSION_LAYERS.map((layer) => {
        const on = open.has(layer);
        return (
          <button
            key={layer}
            type="button"
            data-testid={SESSIONS_TEST_IDS.layerToggle}
            data-layer={layer}
            aria-pressed={on}
            onClick={() => onToggle(layer)}
            className={`inline-flex h-7.5 cursor-pointer items-center gap-1.5 rounded-full border-2 px-3 font-semibold text-small focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit ${
              on
                ? 'border-bleu-nuit bg-bleu-nuit-tint text-bleu-nuit'
                : 'border-gray-200 bg-white text-gray-700'
            }`}
          >
            {/*
              Éteint, la pastille est un ANNEAU et pas un fond : la charte
              refuse `gray-200` et plus sombre comme fond de surface, et le
              garde de `design-system-adherence.test.ts` refuse l'utilitaire nu.
              C'est aussi le traitement des points de statut de cet écran.
            */}
            <span
              aria-hidden="true"
              className={`size-2 rounded-full ${
                on ? 'bg-bleu-nuit' : 'border-2 border-gray-300 bg-white'
              }`}
            />
            {SESSION_LAYER_LABEL[layer]}
          </button>
        );
      })}
      <span className="ml-auto flex gap-3.5 text-small">
        <button
          type="button"
          onClick={onOpenAll}
          className="cursor-pointer font-semibold text-bleu-nuit underline underline-offset-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
        >
          Tout déplier
        </button>
        <button
          type="button"
          onClick={onCloseAll}
          className="cursor-pointer font-semibold text-bleu-nuit underline underline-offset-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
        >
          Tout replier
        </button>
      </span>
    </div>
  );
}
